import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "@fox1ck-org/ui/toast";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  Trash2,
  Key,
  LogOut,
  Shield,
  ShieldOff,
  X,
  Plus,
  Monitor,
  Info,
  Users,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import {
  useUser,
  useUserGroups,
  useUserRoles,
  useUserSessions,
  useUpdateUser,
  useToggleUser,
  useDeleteUser,
  useManageGroup,
  useManageRoles,
  useLogoutUser,
  useResetPassword,
  useAvailableGroups,
  useAvailableRoles,
} from "@/hooks/use-users";
import { UserAccessOverview } from "@/components/users/UserAccessOverview";
import { getKeycloakImpersonationUrl } from "@/lib/auth";
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, CardHeader, CardTitle, cn, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Separator, Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@fox1ck-org/ui";

function formatTimestamp(ts: number) {
  return new Date(ts).toLocaleString();
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading } = useUser(id!);
  const { data: groupsData } = useUserGroups(id!);
  const { data: rolesData } = useUserRoles(id!);
  const { data: sessionsData } = useUserSessions(id!);
  const { data: availableGroupsData } = useAvailableGroups();
  const { data: availableRolesData } = useAvailableRoles();

  const updateUser = useUpdateUser(id!);
  const toggleUser = useToggleUser(id!);
  const deleteUser = useDeleteUser();
  const manageGroup = useManageGroup(id!);
  const manageRoles = useManageRoles(id!);
  const logoutUser = useLogoutUser(id!);
  const resetPassword = useResetPassword(id!);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "" });
  const [showResetPw, setShowResetPw] = useState(false);
  const [pwForm, setPwForm] = useState({ password: "", temporary: true });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);

  const groups = groupsData?.groups ?? [];
  const roles = rolesData?.roles ?? [];
  const sessions = sessionsData?.sessions ?? [];
  const availableGroups = availableGroupsData?.groups ?? [];
  const availableRoles = availableRolesData?.roles ?? [];

  // Filter out already-assigned groups/roles
  const unassignedGroups = availableGroups.filter(
    (g) => !groups.some((ug) => ug.id === g.id)
  );
  const unassignedRoles = availableRoles.filter(
    (r) => !roles.some((ur) => ur.id === r.id) && !r.name.startsWith("default-roles-")
  );

  const visibleRoles = roles.filter((r) => !r.name.startsWith("default-roles-"));

  const startEditing = () => {
    if (!user) return;
    setEditForm({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
    });
    setEditing(true);
  };

  const saveEdit = () => {
    updateUser.mutate(editForm, {
      onSuccess: () => {
        setEditing(false);
        toast.success("Профиль обновлён");
      },
      onError: (err) => toast.error(`Не удалось обновить профиль: ${err.message}`),
    });
  };

  const handleDelete = () => {
    deleteUser.mutate(id!, {
      onSuccess: () => {
        toast.success("Пользователь удалён");
        navigate("/users");
      },
      onError: (err) => toast.error(`Не удалось удалить пользователя: ${err.message}`),
    });
  };

  const handleResetPassword = () => {
    if (!pwForm.password) return;
    resetPassword.mutate(pwForm, {
      onSuccess: () => {
        setShowResetPw(false);
        setPwForm({ password: "", temporary: true });
        toast.success("Пароль сброшен");
      },
      onError: (err) => toast.error(`Не удалось сбросить пароль: ${err.message}`),
    });
  };

  if (isLoading || !user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/users">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 text-lg">
              <AvatarFallback>
                {(user.firstName?.[0] ?? user.username[0] ?? "?").toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-[17px] font-semibold text-foreground">
                {user.firstName || user.lastName
                  ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
                  : user.username}
              </h1>
              <p className="text-sm text-muted-foreground">@{user.username}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowImpersonateConfirm(true)}
            className="bg-amber-600 text-white hover:bg-amber-500"
          >
            <UserCheck className="h-4 w-4" />
            Войти как пользователь
          </Button>
          <Button
            onClick={() =>
              toggleUser.mutate(!user.enabled, {
                onSuccess: () => toast.success(user.enabled ? "Пользователь отключён" : "Пользователь включён"),
                onError: (err) => toast.error(`Ошибка: ${err.message}`),
              })
            }
            disabled={toggleUser.isPending}
            className={cn(
              user.enabled
                ? "bg-yellow-600 text-white hover:bg-yellow-500"
                : "bg-green-600 text-white hover:bg-green-500"
            )}
          >
            {user.enabled ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
            {user.enabled ? "Отключить" : "Включить"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowResetPw(true)}
          >
            <Key className="h-4 w-4" />
            Сбросить пароль
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="h-4 w-4" />
            Удалить
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* User Info */}
        <div className="col-span-2 space-y-6">
          {/* Profile Card */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Профиль</CardTitle>
              {!editing ? (
                <Button variant="secondary" size="sm" onClick={startEditing}>
                  Изменить
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditing(false)}
                  >
                    Отмена
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveEdit}
                    disabled={updateUser.isPending}
                  >
                    <Save className="h-3 w-3" />
                    {updateUser.isPending ? "Сохраняем…" : "Сохранить"}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Имя</label>
                    <Input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Фамилия</label>
                    <Input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-muted-foreground mb-1">Почта</label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  {updateUser.error && (
                    <p className="col-span-2 text-sm text-destructive">{updateUser.error.message}</p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <InfoRow label="Логин" value={user.username} />
                  <InfoRow label="Почта" value={user.email ?? "-"} extra={user.emailVerified ? "Подтверждена" : "Не подтверждена"} />
                  <InfoRow label="Имя" value={user.firstName ?? "-"} />
                  <InfoRow label="Фамилия" value={user.lastName ?? "-"} />
                  <InfoRow label="Статус" value={user.enabled ? "Активен" : "Отключён"} />
                  <InfoRow label="MFA" value={user.totp ? "Включена (TOTP)" : "Не настроена"} />
                  <InfoRow label="Создан" value={formatDate(user.createdTimestamp)} />
                  <InfoRow
                    label="Обязательные действия"
                    value={user.requiredActions?.length ? user.requiredActions.join(", ") : "None"}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Groups */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <CardTitle>Группы</CardTitle>
                {manageGroup.isPending && (
                  <RefreshCw className="h-3.5 w-3.5 text-primary animate-spin" />
                )}
              </div>
              {unassignedGroups.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAddGroup(!showAddGroup)}
                >
                  <Plus className="h-3 w-3" />
                  Добавить группу
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddGroup && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {unassignedGroups.map((g) => (
                    <Button
                      key={g.id}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        manageGroup.mutate(
                          { groupId: g.id, action: "add" },
                          {
                            onSuccess: () => {
                              setShowAddGroup(false);
                              toast.success(`Добавлен в группу «${g.name}»`);
                            },
                            onError: (err) => toast.error(`Не удалось добавить в группу: ${err.message}`),
                          }
                        );
                      }}
                      disabled={manageGroup.isPending}
                      className="border-primary/30 text-primary hover:bg-primary/10"
                    >
                      + {g.name}
                    </Button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {groups.map((g) => (
                  <Badge
                    key={g.id}
                    variant="neutral"
                    className="gap-1.5 px-3 py-1.5 text-sm"
                  >
                    {g.name}
                    <button
                      onClick={() =>
                        manageGroup.mutate(
                          { groupId: g.id, action: "remove" },
                          {
                            onSuccess: () => toast.success(`Удалён из группы «${g.name}»`),
                            onError: (err) => toast.error(`Не удалось удалить из группы: ${err.message}`),
                          }
                        )
                      }
                      disabled={manageGroup.isPending}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {groups.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>Групп нет</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Roles */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <CardTitle>Роли реалма</CardTitle>
                {manageRoles.isPending && (
                  <RefreshCw className="h-3.5 w-3.5 text-primary animate-spin" />
                )}
              </div>
              {unassignedRoles.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAddRole(!showAddRole)}
                >
                  <Plus className="h-3 w-3" />
                  Добавить роль
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {showAddRole && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {unassignedRoles.map((r) => (
                    <Button
                      key={r.id}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        manageRoles.mutate(
                          { roles: [{ id: r.id, name: r.name }], action: "add" },
                          {
                            onSuccess: () => {
                              setShowAddRole(false);
                              toast.success(`Роль «${r.name}» назначена`);
                            },
                            onError: (err) => toast.error(`Не удалось назначить роль: ${err.message}`),
                          }
                        );
                      }}
                      disabled={manageRoles.isPending}
                      className="border-purple-500/30 text-purple-400 hover:bg-purple-600/10"
                    >
                      + {r.name}
                    </Button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <TooltipProvider>
                  {visibleRoles.map((r) => (
                    <Badge
                      key={r.id}
                      variant="neutral"
                      className="gap-1.5 px-3 py-1.5 text-sm"
                    >
                      {r.name}
                      {r.description && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {r.description}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <button
                        onClick={() =>
                          manageRoles.mutate(
                            { roles: [{ id: r.id, name: r.name }], action: "remove" },
                            {
                              onSuccess: () => toast.success(`Роль «${r.name}» снята`),
                              onError: (err) => toast.error(`Не удалось снять роль: ${err.message}`),
                            }
                          )
                        }
                        disabled={manageRoles.isPending}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </TooltipProvider>
                {visibleRoles.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="h-4 w-4" />
                    <span>Ролей реалма нет</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Access Overview */}
          <UserAccessOverview userId={id!} userGroups={groups} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Sessions */}
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Сеансы</CardTitle>
              {sessions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    logoutUser.mutate(undefined, {
                      onSuccess: () => toast.success("Все сеансы завершены"),
                      onError: (err) => toast.error(`Не удалось завершить сеансы: ${err.message}`),
                    })
                  }
                  disabled={logoutUser.isPending}
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  Завершить все
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Monitor className="h-4 w-4" />
                  <span>Активных сеансов нет</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-lg bg-muted/50 p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground font-mono">{s.ipAddress}</span>
                        </div>
                        <button
                          onClick={() =>
                            logoutUser.mutate(s.id, {
                              onSuccess: () => toast.success("Сеанс завершён"),
                              onError: (err) => toast.error(`Ошибка: ${err.message}`),
                            })
                          }
                          disabled={logoutUser.isPending}
                          className="text-muted-foreground hover:text-destructive"
                          title="Завершить сеанс"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Начат: {formatTimestamp(s.start)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Последняя активность: {formatTimestamp(s.lastAccess)}
                      </div>
                      {Object.keys(s.clients).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.values(s.clients).map((clientName, i) => (
                            <Badge
                              key={i}
                              variant="neutral"
                              className="px-1.5 py-0.5 text-[10px]"
                            >
                              {clientName}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card>
            <CardHeader>
              <CardTitle>Кратко</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID</span>
                  <span className="text-foreground font-mono text-xs truncate max-w-[160px]" title={user.id}>
                    {user.id}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Активные сеансы</span>
                  <span className="text-foreground">{sessions.length}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Группы</span>
                  <span className="text-foreground">{groups.length}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Роли</span>
                  <span className="text-foreground">{visibleRoles.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сбросить пароль</DialogTitle>
            <DialogDescription>
              Новый пароль для @{user.username}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Новый пароль</label>
              <Input
                type="password"
                value={pwForm.password}
                onChange={(e) => setPwForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Новый пароль"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="temporary-pw"
                checked={pwForm.temporary}
                onChange={(e) => setPwForm((f) => ({ ...f, temporary: e.target.checked }))}
                className="rounded border-input"
              />
              <label htmlFor="temporary-pw" className="text-sm text-foreground">
                Потребовать смену пароля при следующем входе
              </label>
            </div>
            {resetPassword.error && (
              <p className="text-sm text-destructive">{resetPassword.error.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={handleResetPassword}
              disabled={!pwForm.password || resetPassword.isPending}
              className="w-full"
            >
              {resetPassword.isPending ? "Сбрасываем…" : "Сбросить пароль"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить пользователя</DialogTitle>
            <DialogDescription>
              Удалить пользователя{" "}
              <span className="font-medium text-foreground">@{user.username}</span>? Это нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          {deleteUser.error && (
            <p className="text-sm text-destructive">{deleteUser.error.message}</p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? "Удаляем…" : "Удалить пользователя"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={showImpersonateConfirm} onOpenChange={setShowImpersonateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Вход от имени пользователя</DialogTitle>
            <DialogDescription>
              Откроется админка Keycloak, где можно войти как{" "}
              <span className="font-medium text-foreground">@{user.username}</span>.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-yellow-400">
            Текущий сеанс будет заменён: вы войдёте под этим пользователем во всех приложениях.
            Чтобы вернуться, выйдите и войдите снова.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="secondary"
              onClick={() => setShowImpersonateConfirm(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                setShowImpersonateConfirm(false);
                window.open(getKeycloakImpersonationUrl(id!), "_blank");
              }}
              className="bg-amber-600 text-white hover:bg-amber-500"
            >
              Открыть админку
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function InfoRow({ label, value, extra }: { label: string; value: string; extra?: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground mt-0.5">
        {value}
        {extra && (
          <Badge
            variant={extra === "Подтверждена" ? "ok" : "warn"}
            className="ml-2 text-xs"
          >
            {extra}
          </Badge>
        )}
      </dd>
    </div>
  );
}
